import { formatUserFacingError } from '@/lib/errors'
import { useEffect, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { HoldConfirmButton } from '@/components/ui/hold-confirm-button'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/hooks/useAuth'
import { useSurveyPage } from '../hooks/useSurveyPage'
import { withErrorCode } from '@/lib/errors'
import { testimonialService } from '../services/testimonial.service'
import { StarRatingPicker } from '../components/StarRatingPicker'
import { surveyFormSchema, type SurveyFormValues } from '../types'

export function SurveyPage() {
  const { user } = useAuth()
  const { engagement, alreadySubmitted, eligible, loading, error } = useSurveyPage()
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<SurveyFormValues>({
    resolver: zodResolver(surveyFormSchema),
    defaultValues: {
      clientTitle: '',
      serviceType: '',
      rating: 0,
      content: '',
      consent: undefined as unknown as true,
    },
  })

  const isSubmitting = form.formState.isSubmitting

  // Prefill Service Type from the engagement's first non-addon line item (its
  // description is the service name, e.g. "Promotional Video"). Falls back to
  // the engagement title if there are no line items. Only runs once per
  // engagement so it doesn't clobber edits the client makes.
  useEffect(() => {
    if (!engagement) return
    if (form.getValues('serviceType')) return
    const primaryLine = engagement.lineItems?.find((li) => !li.isAddon)
    const derived = primaryLine?.description ?? engagement.title ?? ''
    if (derived) form.setValue('serviceType', derived)
  }, [engagement, form])

  async function onSubmit(values: SurveyFormValues) {
    if (!engagement || !user) return
    setSubmitError(null)
    try {
      await testimonialService.submitSurvey({
        engagementId: engagement.id,
        clientUserId: user.uid,
        clientName: engagement.clientName,
        clientEmail: engagement.clientEmail,
        clientTitle: values.clientTitle || undefined,
        serviceType: values.serviceType || undefined,
        rating: values.rating,
        content: values.content,
      })
      setSubmitted(true)
    } catch (err) {
      setSubmitError(
        formatUserFacingError(err, 'Something went wrong. Please try again.')
      )
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-2xl">
          <CardHeader className="space-y-4">
            <Skeleton className="mx-auto h-8 w-48" />
            <Skeleton className="h-6 w-full" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Success / Already submitted ───────────────────────────────────────────
  if (submitted || alreadySubmitted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold">Thank You!</h2>
            <p className="text-muted-foreground">
              {alreadySubmitted && !submitted
                ? "We've already received your review for this engagement. Thank you for your feedback!"
                : "We've received your review. Your feedback means a lot to us and helps us improve our services."}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Not eligible / error ───────────────────────────────────────────────────
  if (!eligible || error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="py-12">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="mb-2 text-2xl font-bold">Survey Not Available</h2>
            <p className="text-muted-foreground">
              {error ?? withErrorCode(
                "This survey link isn't valid for your account.",
                'testimonials/invalid-link',
              )}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  // ── Survey form ─────────────────────────────────────────────────────────────
  return (
    <div className="flex min-h-screen items-start justify-center bg-background px-4 py-12">
      <Card className="w-full max-w-2xl">
        <CardHeader className="space-y-2 text-center">
          {/* Branding */}
          <p className="text-sm font-semibold uppercase tracking-widest text-brand-orange">
            ODD Dance Co.
          </p>
          <h1 className="text-2xl font-bold">Share Your Experience</h1>
          {engagement && (
            <p className="text-muted-foreground">
              You&apos;re reviewing:{' '}
              <span className="font-semibold text-foreground">{engagement.title}</span>
            </p>
          )}
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form
              onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}
              className="space-y-6"
            >
              {/* Optional fields */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="clientTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Title / Role <span className="text-muted-foreground">(optional)</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Wedding Couple, Arts Director"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="serviceType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Service Type <span className="text-muted-foreground">(optional)</span></FormLabel>
                      <FormControl>
                        <Input
                          placeholder="e.g. Wedding Photography"
                          disabled={isSubmitting}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              {/* Rating */}
              <FormField
                control={form.control}
                name="rating"
                render={() => (
                  <FormItem>
                    <FormLabel>Overall Rating *</FormLabel>
                    <FormControl>
                      <Controller
                        control={form.control}
                        name="rating"
                        render={({ field }) => (
                          <StarRatingPicker
                            value={field.value}
                            onChange={field.onChange}
                          />
                        )}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Content */}
              <FormField
                control={form.control}
                name="content"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Review *</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Tell us about your experience working with ODD Dance Co.…"
                        rows={6}
                        disabled={isSubmitting}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Consent */}
              <FormField
                control={form.control}
                name="consent"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-lg border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value === true}
                        onCheckedChange={(checked) => {
                          field.onChange(checked === true ? true : undefined)
                        }}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel className="cursor-pointer">
                        I agree to have this review displayed publicly on the ODD Dance Co. website.
                      </FormLabel>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />

              {submitError && (
                <p className="text-sm text-destructive">{submitError}</p>
              )}

              <HoldConfirmButton type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting…' : 'Submit Review'}
              </HoldConfirmButton>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  )
}
